import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setToastHandler, type ToastOptions } from '@/components/ui/use-toast';


export interface Toast {
id: string;
title?: string;
message: string;
variant?: 'default' | 'destructive';
}


interface ToastContextValue {
toasts: Toast[];
push: (t: Omit<Toast, 'id'>) => void;
remove: (id: string) => void;
}


const ToastContext = createContext<ToastContextValue | null>(null);


export function ToastProvider({ children }: { children: React.ReactNode }) {
const [toasts, setToasts] = useState<Toast[]>([]);


const remove = useCallback((id: string) => {
setToasts((list) => list.filter((t) => t.id !== id));
}, []);


const push = useCallback((t: Omit<Toast, 'id'>) => {
const id = Math.random().toString(36).slice(2);
setToasts((list) => [...list, { id, ...t }]);
// auto-hide after 4s
setTimeout(() => remove(id), 4000);
}, [remove]);


const value = useMemo(() => ({ toasts, push, remove }), [toasts, push, remove]);

useEffect(() => {
const handler = (options: ToastOptions) => {
push({
title: options.title,
message: options.description ?? '',
variant: options.variant,
});
};
setToastHandler(handler);
return () => setToastHandler(null);
}, [push]);


return (
<ToastContext.Provider value={value}>
{children}
{/* portal container */}
<div className="fixed bottom-4 right-4 z-50 space-y-2">
{toasts.map((t) => (
<div
key={t.id}
className={`max-w-sm rounded-xl border px-4 py-3 shadow-lg ${
t.variant === 'destructive'
? 'border-destructive/40 bg-destructive text-destructive-foreground'
: 'border-border bg-background/40 text-foreground'
}`}
>
{t.title && <div className="mb-1 font-semibold">{t.title}</div>}
<div className="text-sm">{t.message}</div>
</div>
))}
</div>
</ToastContext.Provider>
);
}


export function useToast() {
const ctx = useContext(ToastContext);
if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
return ctx;
}
